<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import UIKit, { type ITableSortSettings, type ITableColumn, type TTableRow } from '@pocketprep/ui-kit'
import { examModule } from '@/store/exam/module'
import { licenseModule } from '@/store/license/module'
import BrandColors from '@pocketprep/ui-kit/pocketprep-export.module.scss'

const {
    Table,
    Checkbox,
} = UIKit

type ExamMetrics = { [key: string]: { available: number; pending: number; active: number; archived: number } }

type MappedExam = {
    id: string
    name: string
    available: string | number
    pending: number
    active: number
    archived: number
    availableCount: number
    checkboxDisabled: boolean
    nameText: string
    guid: string
    styles: {
        [key: string]: string
    }
}

const props = withDefaults(defineProps<{
    showRowEdit?: boolean
    showCheckboxes?: boolean
    disableSeatlessExams?: boolean
    showFullExamName?: boolean
    enableHoverOnRow?: boolean
    initialCheckedExamIds?: string[]
    theme?: string
    hideArchivedSeats?: boolean
    disabledExamIds?: string[]
}>(), {
    showRowEdit: false,
    showCheckboxes: false,
    disableSeatlessExams: true,
    showFullExamName: false,
    enableHoverOnRow: true,
    initialCheckedExamIds: () => [],
    theme: 'open',
    hideArchivedSeats: false,
    disabledExamIds: () => [],
})

const emit = defineEmits<{
    'checkedRowsChanged': [examCheckedIds: string[]]
}>()

const examCheckedIds = ref<string[]>([])
const examColumns = ref<ITableColumn[]>([{
    name: 'Exam Prep',
    propName: 'name',
    styles: {
        width: props.hideArchivedSeats ? '236px' : '180px',
        display: 'flex',
        alignItems: 'center',
    },
    labelStyles: {
        textAlign: 'left',
        alignItems: 'flex-end',
    },
}, {
    name: 'Available',
    propName: 'available',
}, {
    name: 'Pending',
    propName: 'pending',
}, {
    name: 'Active',
    propName: 'active',
}])
const rowHoverId = ref<string | number | null>(null)
const sortSettings = ref<ITableSortSettings>({ column: examColumns.value[0], direction: 1 })

const currentOrgExams = computed(() => {
    return examModule.getters.getCurrentOrgExams()
})

const examMetadata = computed(() => {
    return examModule.getters.getExamMetadata()
})

const currentOrgLicenses = computed(() => {
    return licenseModule.getters.getCurrentOrgLicenses()
})

const examLicenseMetrics = computed(() => {
    const examMetrics = Object.values(currentOrgExams.value).reduce((acc, exam) => {
        const examMetadataVal = exam && examMetadata.value[exam.examMetadata.objectId]
        const examGuid = examMetadataVal && examMetadataVal.examGuid

        if (!examGuid) {
            return acc
        }

        return {
            ...acc,
            [examGuid]: {
                available: 0,
                pending: 0,
                active: 0,
                archived: 0,
            },
        }
    }, {} as ExamMetrics)

    return currentOrgLicenses.value.reduce(
        (acc, license) => {
            const licenseExamGuid = license.examGuid
            if (!licenseExamGuid || !acc[licenseExamGuid]) {
                return acc
            }

            if (license.status === 'available') {
                acc[licenseExamGuid].available++
            } else if (license.status === 'pending') {
                acc[licenseExamGuid].pending++
            } else if (license.status === 'consumed') {
                acc[licenseExamGuid].active++
            } else if (license.status === 'archived') {
                acc[licenseExamGuid].archived++
            }

            return acc
        },
        examMetrics
    )
})

const mappedExams = computed(() => {
    const sortSettingsVal = sortSettings.value
    return Object.values(currentOrgExams.value).reduce((acc, e) => {
        const examMetadataVal = examMetadata.value[e.examMetadata.objectId]
        const examMetrics = examMetadataVal?.examGuid && examLicenseMetrics.value[examMetadataVal.examGuid]

        if (!examMetrics) {
            return acc
        }

        const checkboxDisabled = (
            props.disableSeatlessExams
            && examMetrics.available <= 0
            && !examMetadataVal?.isFree
        ) || props.disabledExamIds.includes(e.objectId)

        const styles: { [key: string]: string } = {
            cursor: checkboxDisabled || !props.enableHoverOnRow ? 'default' : 'pointer',
            userSelect: 'none',
        }
        if (rowHoverId.value === e.objectId && !checkboxDisabled && props.enableHoverOnRow) {
            styles.backgroundColor = `${BrandColors.grayBackground}60`
        }

        const exam = {
            id: e.objectId,
            name: examMetadataVal.nativeAppName,
            available: examMetadataVal?.isFree ? '∞' : examMetrics.available,
            pending: examMetrics.pending,
            active: examMetrics.active,
            archived: examMetrics.archived,
            availableCount: examMetrics.available,
            checkboxDisabled,
            nameText: examMetadataVal?.nativeAppName,
            guid: examMetadataVal.examGuid,
            styles,
        }
        if (!acc.some(ex => ex.guid === examMetadataVal.examGuid)) {
            acc.push(exam)
        }
        return acc
    }, [] as MappedExam[])
        .sort((a, b) => {
            if (sortSettingsVal?.column && a && b) {
                if (sortSettingsVal.column.name === 'Exam Prep') {
                    return sortSettingsVal.direction * a.nameText.localeCompare(b.nameText)
                }
                if (sortSettingsVal.column.name === 'Available') {
                    return a.available === '∞'
                        ? -1 * sortSettingsVal.direction
                        : b.available === '∞'
                            ? 1 * sortSettingsVal.direction
                            : sortSettingsVal.direction * (b.availableCount - a.availableCount)
                }
                if (sortSettingsVal.column.name === 'Pending') {
                    return sortSettingsVal.direction * (b.pending - a.pending)
                }
                if (sortSettingsVal.column.name === 'Active') {
                    return sortSettingsVal.direction * (b.active - a.active)
                }
                if (sortSettingsVal.column.name === 'Archived') {
                    return sortSettingsVal.direction * (b.archived - a.archived)
                }
            }

            return -1
        })
})

onMounted(async () => {
    if (!props.hideArchivedSeats) {
        examColumns.value = [
            ...examColumns.value,
            {
                name: 'Archived',
                propName: 'archived',
            },
        ]
    }

    if (props.initialCheckedExamIds.length) {
        examCheckedIds.value = props.initialCheckedExamIds
    }

    await Promise.all([
        examModule.actions.fetchCurrentOrgExams(),
        licenseModule.actions.fetchCurrentOrgLicenses(),
        examModule.actions.fetchExamMetadata(),
    ])
})

const updateSortSettings = (sortSettingsVal: ITableSortSettings) => {
    sortSettings.value = sortSettingsVal
}

const rowClicked = (row: { id: string; checkboxDisabled: boolean }) => {
    if (row.checkboxDisabled) {
        return examCheckedIds.value
    }
    if (examCheckedIds.value.includes(row.id)) {
        examCheckedIds.value = examCheckedIds.value.filter(id => id !== row.id)
    } else {
        examCheckedIds.value.push(row.id)
    }
    emit('checkedRowsChanged', examCheckedIds.value)
    return examCheckedIds.value
}

const rowEnter = (row: TTableRow) => {
    rowHoverId.value = row.id
}

const rowLeave = (row: TTableRow) => {
    if (row.id === rowHoverId.value) {
        rowHoverId.value = null
    }
}

const getSeatCellValue = (row: MappedExam, propName: string) => {
    const value = row[propName as keyof MappedExam]
    return value !== null ? value : ''
}
</script>

<template>
    <Table
        :key="`seats-table__${examColumns.length}`"
        :rows="mappedExams"
        :columns="examColumns"
        :default-sort="sortSettings"
        :show-header="false"
        :sort-toggle-order="[ 1, -1 ]"
        class="seats-table"
        :class="{ 'seats-table--condensed': theme === 'condensed' }"
        :table-column-label-styles="{
            margin: '0 auto', textAlign: 'center', display: 'flex',
        }"
        :table-cell-styles="{ margin: '0 auto', width: '64px', height: theme === 'condensed' ? '30px' : '40px' }"
        :table-sort-icon-styles="{
            position: 'absolute', top: '50%', right: '-15px', marginTop: '-4.5px'
        }"
        @rowEnter="rowEnter"
        @rowLeave="rowLeave"
        @sort="updateSortSettings"
        @rowClicked="rowClicked({
            id: $event.id as string,
            checkboxDisabled: $event.checkboxDisabled as boolean
        })"
    >
        <template #tableColumnLabelText="{ column }">
            <div v-html="column.name" />
        </template>
        <template #tableCellValue="{ row, column }">
            <div
                v-if="column.propName === 'name' && showFullExamName"
                class="seats-table__name"
            >
                {{ row.name }}
            </div>
            <div
                v-else-if="column.propName === 'name' && showCheckboxes"
                class="seats-table__checkbox-name"
            >
                <Checkbox
                    :modelValue="examCheckedIds.includes(row.id)"
                    class="seats-table__checkbox"
                    :disabled="row.checkboxDisabled"
                />
                <div class="seats-table__name">
                    {{ row.name }}
                </div>
            </div>
            <div
                v-else
                class="seats-table__count"
                :class="{
                    [`seats-table__count--${column.propName}`]: true,
                    'seats-table__count--condensed': theme === 'condensed'
                }"
                v-html="getSeatCellValue(row, column.propName)"
            />
        </template>
    </Table>
</template>

<style lang="scss" scoped>


.seats-table {
    &__count {
        width: 64px;
        height: 40px;
        font-size: 16px;
        line-height: 19px;
        font-weight: 600;
        display: flex;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        border-radius: 3px;
        margin-top: 0;

        &--condensed {
            height: 30px;
        }
    }

    &__count--available {
        background: $lovely-euphoric-delight;
        color: $barney-purple;
    }

    &__count--pending {
        background: $manilla;
        color: $golden-hind;
    }

    &__count--active {
        background: $green-hint;
        color: $peacock-green;
    }

    &__count--archived {
        background: $gray-background;
        color: $slate-01;
    }

    &__name {
        &:deep(.sublabel) {
            display: block;
            font-size: 13px;
            line-height: 20px;
            font-weight: 500;
            color: $slate-03;
        }
    }

    &__checkbox-name {
        display: flex;
        align-items: center;
    }

    &__checkbox {
        margin-right: 10px;
    }
}
</style>
